import { authReducer } from '@auth/state';
import { classificationReducer } from '@classification-testing/state';
import { getReduxLoggerConfig } from '@core/logging/reduxConfig';
import { createReduxLoggerMiddleware } from '@core/logging/reduxMiddleware';
import { coreReducer } from '@core/state';
import { configureStore } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import type { PersistPartial } from 'redux-persist/es/persistReducer';
import storage from 'redux-persist/lib/storage';

// Define the state types
export type AuthState = ReturnType<typeof authReducer>;
export type PersistedAuthState = AuthState & PersistPartial;

// Define the classification testing state type
export type ClassificationState = ReturnType<typeof classificationReducer>;
export type PersistedClassificationState = ClassificationState & PersistPartial;

// Define the core state type
export type CoreState = ReturnType<typeof coreReducer>;
export type PersistedCoreState = CoreState & PersistPartial;

// Define the root state type
export interface RootState {
  auth: PersistedAuthState;
  classification: ClassificationState;
  core: PersistedCoreState;
}

// Export the Provider component
export { Provider } from './Provider';

// Configure persistence specifically for auth state
const authPersistConfig = {
  key: 'auth',
  storage,
  whitelist: ['user', 'isAuthenticated'], // Only persist non-sensitive auth data
  blacklist: [],
};

// Configure persistence specifically for classification testing state
const classificationPersistConfig = {
  key: 'classification',
  storage,
  whitelist: [], // No persistent state needed for classification testing
  blacklist: ['classification'], // Don't persist transient classification state
};

// Configure persistence specifically for core state
const corePersistConfig = {
  key: 'core',
  storage,
  whitelist: ['theme'], // Persist the theme preference
  blacklist: ['settingsDialog', 'theme.completeTheme'], // Don't persist dialog state or complete theme object
};

// Configure store with direct reducer mapping
export const store = configureStore({
  reducer: {
    auth: persistReducer(authPersistConfig, authReducer),
    classification: persistReducer(classificationPersistConfig, classificationReducer),
    core: persistReducer(corePersistConfig, coreReducer),
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore persistence actions in serializability check
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(createReduxLoggerMiddleware(getReduxLoggerConfig())),
  devTools: process.env.NODE_ENV !== 'production',
});

// Create persistor
export const persistor = persistStore(store);

// Export types for dispatch
export type AppDispatch = typeof store.dispatch;

// Export typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Helper functions to access store state outside of React components
 * This is useful for route guards and other non-React code
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getState = (): any => store.getState();
export const dispatch = store.dispatch;
